import { MultiselectOption } from '@cbo/shared-library';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import React, { ReactNode } from 'react';
import Header from '../Header/Header';
import Multiselect from '../GlobalFilterBar/Multiselect/Multiselect';
import { controlsRowSx, headerActionsContainerSx, rootGridSx, searchBarSx, sectionsGridSx } from './styles';
import { pagePadding } from '../../lib/styles/commonStyles';
import CentralDashboardSection from './components/CentralDashboardSection';

export type CentralDashboardMultiselectFilterConfig = {
  controllerName: string;
  options: MultiselectOption[];
  singular: string;
  plural: string;
  titleText: string;
  defaultChecked?: boolean;
  loading?: boolean;
  disabled?: boolean;
  disabledLabel?: string;
};

export type CentralDashboardFiltersConfig = {
  categories?: CentralDashboardMultiselectFilterConfig;
  statuses?: CentralDashboardMultiselectFilterConfig;
  vendors?: CentralDashboardMultiselectFilterConfig;
};

export type CentralDashboardSearchConfig = {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  testId?: string;
  disabled?: boolean;
};

export type CentralDashboardCardData = {
  id: string;
  title: string;
  description?: string;
  onClick?: () => void;
  testId?: string;
  url?: string;
  isLoading?: boolean;
  moreOptionsMenuItems?: {
    id: string;
    label: string;
    onClick: () => void;
    testId?: string;
  }[];
};

export type CentralDashboardSectionData = {
  id: string;
  title: string;
  cards: CentralDashboardCardData[];
  hasMorePages?: boolean;
  isLoading?: boolean;
};

export type CentralDashboardProps = {
  title?: string;
  description?: string;
  headerTestId?: string;
  headerMaxDescriptionWidth?: string;
  headerActions?: ReactNode;
  controlsTestId?: string;
  filters?: CentralDashboardFiltersConfig;
  search?: CentralDashboardSearchConfig;
  sections?: CentralDashboardSectionData[];
  children?: ReactNode;
  loading?: boolean;
  loadingTestId?: string;
  containerRef?: React.Ref<HTMLDivElement>;
  usePagePadding?: boolean;
  onSectionViewMore?: (sectionId: string) => void;
};

function CentralDashboard({
  title,
  description,
  headerTestId,
  headerMaxDescriptionWidth = '100%',
  headerActions,
  controlsTestId,
  filters,
  search,
  sections,
  children,
  loading = false,
  loadingTestId = 'spinner-container',
  containerRef,
  usePagePadding = true,
  onSectionViewMore,
}: Readonly<CentralDashboardProps>) {
  const theme = useTheme();

  if (loading) {
    return (
      <Grid container alignItems='center' justifyContent='center' data-testid={loadingTestId}>
        <CircularProgress />
      </Grid>
    );
  }

  const hasBuiltInFilters = Boolean(filters?.categories) || Boolean(filters?.statuses) || Boolean(filters?.vendors);
  const hasControls = hasBuiltInFilters || Boolean(search);

  return (
    <Grid sx={usePagePadding ? { ...rootGridSx, ...pagePadding } : rootGridSx} ref={containerRef} tabIndex={-1}>
      <Grid
        container
        justifyContent='space-between'
        alignItems='flex-start'
        sx={{
          mt: 2,
          mb: 1,
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          borderRadius: theme.shape.borderRadius,
        }}
      >
        {title && (
          <Grid item xs={8}>
            <Header
              maxDescriptionWidth={headerMaxDescriptionWidth}
              dataTestId={headerTestId ?? 'central-dashboard-header'}
              title={title}
              description={description}
            />
          </Grid>
        )}
        {headerActions && (
          <Grid item sx={headerActionsContainerSx}>
            {headerActions}
          </Grid>
        )}
      </Grid>

      {hasControls && (
        <Grid item xs={12} sx={controlsRowSx} data-testid={controlsTestId}>
          <Grid gap={2} container>
            {!!filters?.categories && (
              <Multiselect
                controllerName={filters.categories.controllerName}
                options={filters.categories.options}
                singular={filters.categories.singular}
                plural={filters.categories.plural}
                titleText={filters.categories.titleText}
                defaultChecked={filters.categories.defaultChecked ?? false}
                loading={filters.categories.loading ?? false}
              />
            )}
            {!!filters?.statuses && (
              <Multiselect
                controllerName={filters.statuses.controllerName}
                options={filters.statuses.options}
                singular={filters.statuses.singular}
                plural={filters.statuses.plural}
                titleText={filters.statuses.titleText}
                defaultChecked={filters.statuses.defaultChecked ?? false}
                loading={filters.statuses.loading ?? false}
              />
            )}
            {!!filters?.vendors &&
              (filters.vendors.disabled ? (
                <Button variant='outlined' size='small' disabled>
                  {filters.vendors.disabledLabel ?? filters.vendors.plural}
                </Button>
              ) : (
                <Multiselect
                  controllerName={filters.vendors.controllerName}
                  options={filters.vendors.options}
                  singular={filters.vendors.singular}
                  plural={filters.vendors.plural}
                  titleText={filters.vendors.titleText}
                  defaultChecked={filters.vendors.defaultChecked ?? false}
                  loading={filters.vendors.loading ?? false}
                />
              ))}
          </Grid>

          {!!search && (
            <TextField
              data-testid={search.testId ?? 'central-dashboard-search'}
              variant='outlined'
              placeholder={search.placeholder}
              size='small'
              disabled={search.disabled}
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              sx={searchBarSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: search.value ? (
                  <InputAdornment position='end'>
                    <IconButton aria-label='clear search' onClick={() => search.onChange('')} edge='end' size='small'>
                      <CancelIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          )}
        </Grid>
      )}

      <Grid item xs={12} sx={sectionsGridSx}>
        {sections?.length
          ? sections.map((section) => (
              <CentralDashboardSection
                key={section.id}
                section={section}
                onViewMore={() => onSectionViewMore?.(section.id)}
              />
            ))
          : children}
      </Grid>
    </Grid>
  );
}

export default CentralDashboard;
